import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { FaArrowRight } from "react-icons/fa";
import { PropagateLoader } from "react-spinners"
import { MdDelete, MdEdit } from "react-icons/md";
import DataTable from "react-data-table-component";
import { useNavigate, useParams } from "react-router-dom";
import NoData from "../../../atoms/NoData";
import Button from "../../../atoms/Button";
import Modal from "../../../molecules/Modal";
import ModalConfirm from "../../../molecules/ModalConfirm";
import ModalEditCaso from "./ModalEditCaso";
import FilterCasos from "./FilterCasos";
import useModal from "../../../../hooks/useModal";
import { DELETE_CASO } from "../../../../api/apiCasos";
import moment from "moment/moment";
import "moment/locale/es";

const TableCasos = ({ data, refetch, loading, user, stateOptions }) => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [caso, setCaso] = useState();
    const [filteredData, setFilteredData] = useState([]);
    const [activeFilters, setActiveFilters] = useState(null); // Guardar los filtros activos
    const { open, close, isOpen } = useModal();
    const { open: openDelete, close: closeDelete, isOpen: isOpenDelete } = useModal();
    moment.locale("es");

    // NUEVO: Sincronizar filteredData cuando data cambia
    useEffect(() => {
        if (!data || data.length === 0) {
            setFilteredData([]);
            return;
        }
        
        // Si hay filtros activos, reaplicarlos a los nuevos datos
        if (activeFilters) {
            handleFilterChange(data, activeFilters);
        } else {
            // Si no hay filtros, mostrar todos los datos
            setFilteredData(data);
        }
    }, [data, activeFilters]);

    const handleFilterChange = (filtered, filters = null) => {
        setFilteredData(filtered);
        setActiveFilters(filters); // Guardar filtros activos
    };

    const handlePush = (idCaso) => {
        navigate(`/clientes/empresas/${id}/defensa_fiscal/${idCaso}`);
    };

    const handleOpenEdit = (caso) => {
        setCaso(caso);
        open();
    };

    const handleOpenDelete = (caso) => {
        openDelete();
        setCaso(caso);
    };

    const handleClose = () => {
        close();
        closeDelete();
        setCaso(null);
    };

    const handleDelete = async () => {
        try {
            await DELETE_CASO(caso.id);
            await refetch?.();
            handleClose();
            toast.success("Caso eliminado correctamente");
        } catch (error) {
            console.error(error);
            toast.error("Error al eliminar el caso");
        }
    };

    const BadgeStyles = {
        authority: "bg-orange-100 text-orange-800",
        origin: "bg-violet-100 text-violet-800",
        concept: "bg-indigo-100 text-indigo-800",
        state: "bg-emerald-100 text-emerald-800",
        comment: "bg-slate-100 text-slate-800",
        status: "bg-lime-100 text-lime-800",
        period: "bg-fuchsia-100 text-fuchsia-800",
    };

    const Badge = ({ text, variant = "default" }) => (
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${BadgeStyles[variant] || "bg-gray-100 text-gray-800"}`}>
            {text}
        </span>
    );

    const columns = [
        {
            name: "Caso",
            selector: row => (
                <div className="py-2 gap-2 flex flex-col">
                    <div className="font-bold text-gray-900">{row?.name}</div>
                    <div className="flex flex-wrap gap-2">
                        {row?.authority && <Badge text={row.authority} variant="authority" />}
                        {row?.origin && <Badge text={row.origin} variant="origin" />}
                        {row?.concept?.map((item, index) => (
                            <Badge key={`concept-${index}`} text={item} variant="concept" />
                        ))}
                        {row?.state && <Badge text={row.state} variant="state" />}
                        {row?.comment && <Badge text={row.comment} variant="comment" />}
                    </div>
                    <div className="flex flex-wrap gap-2 pt-1">
                        {row?.status && <Badge text={row.status} variant="status" />}
                        {row?.startPeriod && row?.endPeriod && (
                            <Badge 
                                text={`${moment(row.startPeriod).format("DD MMM YYYY")} - ${moment(row.endPeriod).format("DD MMM YYYY")}`}
                                variant="period" 
                            />
                        )}
                    </div>
                </div>
            ),
        },
        {
            name: "Acciones",
            cell: row => (
                <div className="flex flex-row space-x-2">
                    {user && user.role !== "Client" &&
                        <>
                            <Button text={<MdEdit size={20} />} bgColor="blue" width="w-10" onClick={() => handleOpenEdit(row)} title="Editar caso" />
                            <Button text={<MdDelete size={20} />} bgColor="red" width="w-10" onClick={() => handleOpenDelete(row)} title="Eliminar caso" />
                        </>
                    }
                    <Button text={<FaArrowRight size={18} />} bgColor="orange" width="w-10" onClick={() => handlePush(row.id)} title="Ver detalles" />
                </div>
            ),
        },
    ];

    // SIMPLIFICADO: Solo mostrar filteredData
    const displayData = filteredData.length > 0 ? filteredData : (data && data.length === 0 ? [] : data || []);

    return (
        <>
            <FilterCasos 
                data={data || []} 
                onFilterChange={handleFilterChange}
                stateOptions={stateOptions}
            />
            <DataTable
                data={displayData}
                columns={columns}
                progressPending={loading}
                progressComponent={<PropagateLoader color="red" />}
                noDataComponent={<NoData />}
                pagination
            />
            {isOpen && caso &&
                <Modal onClose={handleClose} title="Editar Caso">
                    <ModalEditCaso caso={caso} onClose={handleClose} refetch={refetch} />
                </Modal>
            }
            {isOpenDelete && caso &&
                <ModalConfirm onClose={handleClose} onConfirm={handleDelete} eliminacion />
            }
        </>
    );
};

export default TableCasos;